"use client";

import { route as brandbrandTokenRoute } from "@faire/web-api/routes/brand/brandToken";
import { route as flashSaleRoute } from "@faire/web-api/routes/flash-sale";
import { route as posHomeRoute } from "@faire/web-api/routes/point-of-sale";
import { route as posAboutRoute } from "@faire/web-api/routes/point-of-sale/about";
import { debounceIfPossible } from "@faire/web/common/globals/debounceIfPossible";
import {
  getWindow,
  getWindowOrThrow,
} from "@faire/web/common/globals/getWindow";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { removeLocaleFromUrl } from "@faire/web/ui/routing/util";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import React, { Suspense, useCallback, useEffect, useMemo, useRef } from "react";
import { matchPath } from "react-router-dom";

import { useZustandLocation } from "@faire/retailer/app/_lib/routing/useZustandLocation";
import { isIdentityVerificationModalOrRoute } from "@faire/retailer/components/App/AppModals/utils";
import { useShouldShowSaleAnnouncementTooltip } from "@faire/retailer/components/Header/desktop/SaleAnnouncementTooltip/useShouldShowSaleAnnouncementTooltip";
import { useNotificationPreview } from "@faire/retailer/components/Header/HeaderNotifications/hooks/useNotificationPreview";
import { useNotificationRefetch } from "@faire/retailer/components/Header/HeaderNotifications/hooks/useNotificationRefetch";
import {
  HeaderPriorityProvider,
  useHeaderPriorityStore,
} from "@faire/retailer/components/HeaderPriorityRenderer/HeaderPriorityPriovider";
import { LazyToastContainer } from "@faire/retailer/components/Toast";
import { AppNudgeProvider } from "@faire/retailer/features/Nudges/useAppNudgeStore";
import { usePOSSummary } from "@faire/retailer/features/PointOfSale/hooks/usePOSSummary";
import { useIsLoggedInRetailer } from "@faire/retailer/lib/isLoggedInRetailer";
import { isMobileApp } from "@faire/retailer/lib/isMobileApp";
import { useStickyNavContentPositions } from "@faire/retailer/navbar/useStickyNavContentPositions";
import { useMarketStore } from "@faire/retailer/stores/domain/Events/FaireMarkets/MarketStore";
import { usePromoBanners } from "@faire/retailer/stores/ui/usePromoBanners";
import { useTopOfPageContent } from "@faire/retailer/stores/ui/useTopOfPageContent";

import * as styles from "./WrapperStyles.css";

interface WrapperProps {
  children: React.ReactNode;
  hasNoTopBanner?: boolean;
  hasToastContainer?: boolean;
}

export const TopOfPageContentWrapper: React.FC<WrapperProps> = ({
  children,
  hasNoTopBanner,
  hasToastContainer,
}) => {
  useNotificationRefetch();
  const notificationPreview = useNotificationPreview();
  return (
    <HeaderPriorityProvider
      value={{ isNotificationPreviewVisible: !!notificationPreview }}
    >
      <TopOfPageContentWrapperBase
        hasNoTopBanner={hasNoTopBanner}
        hasToastContainer={hasToastContainer}
      >
        {children}
      </TopOfPageContentWrapperBase>
    </HeaderPriorityProvider>
  );
};

const TopOfPageContentWrapperBase: React.FC<WrapperProps> = ({
  children,
  hasNoTopBanner,
  hasToastContainer,
}) => {
  const { isDesktopAndAbove } = useViewport();
  const contentRef = useRef<HTMLDivElement | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const { shouldRetainOriginalPosition } = useStickyNavContentPositions();
  const shouldShowSaleAnnouncementTooltip =
    useShouldShowSaleAnnouncementTooltip();

  const [{ isNotificationPreviewVisible }] = useHeaderPriorityStore([
    "isNotificationPreviewVisible",
  ]);
  const shouldHideTopNav = useIsLoggedInRetailer();
  const { setHeaderHeight } = useTopOfPageContent();
  const { bannerHeight } = usePromoBanners();
  const { isShowingCountdownBanner: isShowingMarketCountdownBanner } =
    useMarketStore(["isShowingCountdownBanner"]);
  const notificationPreview = useNotificationPreview();
  const { hasLinkedPOS } = usePOSSummary();

  const { pathname: pathnameWithMaybeLocale } = useZustandLocation(
    (state) => state.pathname
  );

  const pathname = useMemo(
    () => removeLocaleFromUrl(pathnameWithMaybeLocale),
    [pathnameWithMaybeLocale]
  );

  const nonStickyHeader: boolean = useMemo(() => {
    const nonStickyHeaderPath = [
      {
        path: brandbrandTokenRoute,
        exact: true,
      },
      {
        path: flashSaleRoute,
        exact: true,
      },
      {
        path: posAboutRoute,
        exact: true,
      },
      ...(!hasLinkedPOS
        ? [
            {
              path: posHomeRoute,
              exact: true,
            },
          ]
        : []),
    ].filter(isNotUndefined);
    return nonStickyHeaderPath.some(
      (path) => matchPath(pathname, path) !== null
    );
  }, [pathname, hasLinkedPOS]);

  const handleResize = useCallback(() => {
    setHeaderHeight(contentRef.current?.clientHeight ?? 0);
  }, [setHeaderHeight]);

  const debouncedHandleResize = useMemo(
    () =>
      debounceIfPossible(() => {
        handleResize();
      }, 500),
    [handleResize]
  );

  useEffect(() => {
    getWindow()?.addEventListener("resize", debouncedHandleResize);
    return () => {
      getWindow()?.removeEventListener("resize", debouncedHandleResize);
    };
  }, [debouncedHandleResize]);

  useEffect(() => {
    if (!shouldHideTopNav || nonStickyHeader) {
      return;
    }

    if (shouldRetainOriginalPosition) {
      setHeaderHeight(contentRef.current?.clientHeight ?? 0);
    } else {
      setHeaderHeight(0);
    }
  }, [
    shouldHideTopNav,
    shouldRetainOriginalPosition,
    nonStickyHeader,
    setHeaderHeight,
  ]);

  useEffect(() => {
    return () => {
      if (contentRef.current) {
        resizeObserverRef.current?.unobserve(contentRef.current);
      }
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
      contentRef.current = null;
    };
  }, []);

  const contentNodeCallback = useCallback(
    (node: HTMLDivElement | null) => {
      if (node) {
        const observer = getWindowOrThrow().ResizeObserver
          ? new (getWindowOrThrow().ResizeObserver)((entry) => {
              getWindowOrThrow().requestAnimationFrame(() => {
                if (!entry) {
                  return;
                }
                setHeaderHeight(entry[0]?.contentRect.height ?? 0);
              });
            })
          : undefined;
        observer?.observe(node);
        contentRef.current = node;
        setHeaderHeight(contentRef.current.clientHeight);
        if (observer) {
          resizeObserverRef.current = observer;
        }
      }
    },
    [setHeaderHeight]
  );

  const style: React.CSSProperties | undefined = useMemo(() => {
    if (!nonStickyHeader) {
      // AddedToBag must not get unmounted when the `hideHeadersAndBanners` prop
      // changes, or otherwise we don't correctly show the added to bag toast
      // after adding a product to cart from the mobile product modal.

      const topPosition =
        isDesktopAndAbove && isShowingMarketCountdownBanner && !hasNoTopBanner
          ? bannerHeight
          : 0;

      // Preview has an absolute position, so we need to account for it
      const notificationPreviewOffset =
        notificationPreview && isNotificationPreviewVisible ? 100 : 0;

      const salesTooltipOffset = shouldShowSaleAnnouncementTooltip ? 188 : 0;

      const absoluteTopElementsOffset =
        notificationPreviewOffset + salesTooltipOffset;

      const stickyContentHeight = contentRef.current?.clientHeight
        ? contentRef.current.clientHeight + absoluteTopElementsOffset
        : undefined;
      const hideComponentOffsetTop =
        stickyContentHeight ?? styles.HIDE_COMPONENT_OFFSET_TOP;
      return assignInlineVars({
        [styles.varTop]: shouldRetainOriginalPosition
          ? `${topPosition}px`
          : `-${hideComponentOffsetTop}px`,
        [styles.varToastTop]: shouldRetainOriginalPosition
          ? `${topPosition + hideComponentOffsetTop}px`
          : "0px",
        [styles.varTransitionDuration]: `${
          (hideComponentOffsetTop / styles.HIDE_COMPONENT_OFFSET_TOP) * 400
        }ms`,
        [styles.varTransitionTimingFunction]: shouldRetainOriginalPosition
          ? styles.TRANSITION_IN_FUNCTION
          : styles.TRANSITION_OUT_FUNCTION,
      });
    }
    return undefined;
  }, [
    bannerHeight,
    hasNoTopBanner,
    isDesktopAndAbove,
    isNotificationPreviewVisible,
    isShowingMarketCountdownBanner,
    nonStickyHeader,
    notificationPreview,
    shouldRetainOriginalPosition,
    shouldShowSaleAnnouncementTooltip,
  ]);

  if (isMobileApp() && isIdentityVerificationModalOrRoute()) {
    return null;
  }

  return (
    <>
      <AppNudgeProvider>
        <div
          data-test-id="header-content"
          ref={contentNodeCallback}
          className={nonStickyHeader ? styles.nonSticky : styles.sticky}
          style={style}
        >
          {children}
        </div>
      </AppNudgeProvider>
      {hasToastContainer ? (
        <div className={styles.hiddenSticky} style={style}>
          <Suspense fallback={null}>
            <LazyToastContainer />
          </Suspense>
        </div>
      ) : null}
    </>
  );
};
